// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import * as React from 'react';
import ReactDOM from 'react-dom';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import { FlexGridXlsxConverter } from 'wijmo/wijmo.grid.xlsx';
import { CellType } from 'wijmo/wijmo.grid';
import { Workbook } from 'wijmo/wijmo.xlsx';

import type { CommonActionProps } from './types.actions';
import ButtonAction from './ButtonAction';
import messages from '../../config/messages';
import { generateFileName } from '../../utils';
import { DesktopView, MobileView } from '../../config/mediaQuery';

const rootElem = document.getElementById('root') ||
  document.body || {
    // eslint-disable-next-line
    appendChild: any => {},
    // eslint-disable-next-line
    removeChild: any => {},
  };

class BlockingActionIndicator extends React.PureComponent<{
  title: string,
  visible: boolean,
}> {
  el = document.createElement('div');

  componentDidMount() {
    rootElem.appendChild(this.el);
  }

  componentWillUnmount() {
    rootElem.removeChild(this.el);
  }

  render() {
    if (!this.props.visible) {
      return null;
    }

    return ReactDOM.createPortal(
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 999999,
        }}
      >
        <div
          css={{
            backgroundColor: 'rgb(20, 156, 130, 0.8)',
            padding: '7px',
            color: 'white',
            fontSize: '18px',
          }}
        >
          {this.props.title}
        </div>
      </div>,
      this.el,
    );
  }
}

const exportToExcel = async (
  grid: any,
  fileName: string,
  onSuccess: () => mixed,
  onError: any => mixed,
  setExportProgress: (number | null) => void
) => {

  // Implementation Upc cell custom formating
  // based off this ticket
  // https://www.grapecity.com/my-account/my-support/case/CAS-40809-Y3N2D6
  let upcIndex = null
  function exportFormatItem(args) {
    if (upcIndex === null) {
      upcIndex = args.panel.columns.findIndex(c => c.binding === 'Upc');
    }
    const cellCol = args.col
    if (cellCol === upcIndex && args.panel.cellType === CellType.Cell) {
        args.xlsxCell.style.format = Workbook.toXlsxNumberFormat('g0');
    }
  }

   return new Promise((resolve, reject) => {
    const { pageSize, pageIndex } = grid.itemsSource;

    /**
     * We need to import all the content, so without this, we'll be
     * exporting only the current page
     */
    grid.itemsSource.pageSize = grid.itemsSource.totalItemCount;

    FlexGridXlsxConverter.saveAsync(
      grid,
      {
        includeColumnHeaders: true,
        includeStyles: true,
        includeColumns: column => column.visible,
        formatItem: exportFormatItem
      },
      fileName,
      () => {
        grid.itemsSource.pageSize = pageSize;
        grid.itemsSource.moveToPage(pageIndex);

        if (setExportProgress) {
          setExportProgress(null);
        }

        resolve();
        onSuccess();
      },
      e => {
        grid.itemsSource.pageSize = pageSize;
        grid.itemsSource.moveToPage(pageIndex);

        reject(e);
        onError(e);
      },
      (progressValue) => {
        if (setExportProgress && Number(progressValue) % 5 === 0) {
          setExportProgress(progressValue);
        }
      },
      true // async grid during export
    );
  });
};

type Props = {
  moduleName: string,
  grid: any,
  disabled?: boolean,
  disabledDelay: number,
  exportToExcel: (any, string, () => mixed, (any) => mixed) => mixed,
};

type State = {
  status: string,
  exportProgress: number | null,
};

class ExportAction extends React.Component<CommonActionProps & Props, State> {
  static defaultProps = {
    size: 'xsmall',
    disabledDelay: 500,
    exportToExcel,
    disabled: false,
  };

  state = {
    status: '',
    exportProgress: null,
  };

  setExportProgress = (progress: number | null) => {
    this.setState(prevState => ({
      ...prevState,
      exportProgress: progress
    }))
  }

  exportToExcel = (options: { grid: any, moduleName: string }) => () => {
    const { grid, moduleName } = options;
    const fileName = generateFileName(moduleName);

    this.setState(
      {
        status: 'exporting',
      },
      async () => {
        /**
         * The exportToExcel is an extremely intense an blocking action, so we need
         * to give React some space to perform the update. If we don't wait for a bit
         * before triggering that action we might not even be able to show it.
         */
        await new Promise(resolve => {
          setTimeout(resolve, 250);
        });

        await this.props.exportToExcel(
          grid,
          fileName,
          () => {
            this.setState({
              status: 'exported',
            });
          },
          () => {
            this.setState({
              status: 'failed',
            });
          },
          this.setExportProgress
        );

        setTimeout(() => {
          this.setState({
            status: '',
          });
        }, this.props.disabledDelay);
      },
    );
  };

  render() {
    const disabled = this.state.status !== '' || this.props.disabled;

    const exportProgressMessage = this.state.exportProgress !== null 
    ? `${messages.common.exporting} ${this.state.exportProgress}%`
    : messages.common.exporting;


    return (
      <React.Fragment>
        <DesktopView>
          <ButtonAction
            onClick={this.exportToExcel({
              grid: this.props.grid,
              moduleName: this.props.moduleName,
            })}
            bsSize={this.props.size}
            disabled={disabled}
          >
            <Glyphicon glyph="file" /> Export
          </ButtonAction>
        </DesktopView>
        <MobileView>
          <button 
            onClick={this.exportToExcel({
              grid: this.props.grid,
              moduleName: this.props.moduleName,
            })}
            disabled={disabled}
          >
            <img src='assets/file-bottom.svg' alt='file icon'/> Export
          </button>
        </MobileView>
        <BlockingActionIndicator
          visible={this.state.status === 'exporting'}
          title={exportProgressMessage}
        />
      </React.Fragment>
    );
  }
}

export default ExportAction;
